import React from 'react';
import { Box, Grid, Typography, Divider, Tooltip, Chip, Paper, Button } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ChevronRight from '@mui/icons-material/ChevronRight';
import LockIcon from '@mui/icons-material/Lock';
import dataMock from '../dataMock';
import {
  ResponsiveContainer,
  BarChart as ReBarChart,
  Bar as ReBar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend as ReLegend
} from 'recharts';
import { buildMarketDistribution, buildCagrData, marketChartData, marketNumber, formatMarketPercent } from '../adapters/marketV2Adapter';

const REGION_COLORS = {
  Europe: '#dc2626', // red-600
  'North America': '#f97316', // orange-500
  'East Asia': '#16a34a', // green-600
  'Southeast Asia': '#f59e0b', // amber-500 (used for LatAm in ref; adjusted)
  Oceania: '#2563eb', // blue-600
  LatAm: '#22c55e', // green-500
  'Middle East': '#06b6d4', // cyan-500
  Africa: '#14532d' // green-900
};

function Legend({ items }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {items.map((item) => (
        <Box key={item.region} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 4, height: 40, bgcolor: REGION_COLORS[item.region] || '#6b7280' }} />
          <Typography variant="body2" fontWeight={600} noWrap>
            {item.region}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function VerticalBar({ items, height = 600 }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: 64, height, border: '1px solid', borderColor: 'divider' }}>
      {items.map((item) => (
        <Tooltip key={item.region} title={`${item.region} — ${formatMarketPercent(item.percentage)}`} placement="right" arrow>
          <Box
            sx={{
              height: `${(item.percentage / 100) * height}px`,
              bgcolor: REGION_COLORS[item.region] || '#6b7280',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700
            }}
          >
            {item.percentage >= 4 ? `${item.percentage}%` : null}
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}

function Footnotes({ items }) {
  if (!items?.length) return null;
  return (
    <Box sx={{ mt: 1.5 }}>
      {items.map((item) => (
        <Typography key={item.region} variant="caption" display="block">
          {item.region} — {item.percentage}%
        </Typography>
      ))}
    </Box>
  );
}

export default function MarketOverviewV2({ data, allowAnyRegions, isProUser, handleTrialClick, trialAllowed }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  //   const driversText = React.useMemo(() => {
  //     try {
  //       const rg = dataMock?.RegionalGrowth || {};
  //       const order = ['North America', 'Europe', 'East Asia'];
  //       const texts = order.map((r) => rg[r]?.Drivers).filter(Boolean);
  //       return texts.join(' ');
  //     } catch (_) {
  //       return '';
  //     }
  //   }, []);
  const distribution = buildMarketDistribution(data, { allowAnyRegions });
  if (distribution.unsupported) {
    return (
      <Box>
        <Typography variant="body2" color="error">
          Unsupported regions in payload: {distribution.unknown.join(', ')}. Rendering legacy view.
        </Typography>
      </Box>
    );
  }

  // Use dynamic formatter (M/B/T) instead of forcing trillions
  const cagrRows = buildCagrData(data);

  const formatMarketSize = (value) => {
    const num = marketNumber(value);
    if (num === null) return '';
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toFixed(2)}`;
  };

  const mainChartData = marketChartData(data);
  const relatedChart = mainChartData.length === 0
    ? data?.RelatedMarketEvidence?.find((chart) => marketChartData({ MarketSizeEvidence: chart }).length > 0)
    : null;
  const chartEvidence = relatedChart || data?.MarketSizeEvidence;
  const projectionData = relatedChart ? marketChartData({ MarketSizeEvidence: relatedChart }) : mainChartData;
  const chartBaseYear = chartEvidence?.base_year || new Date().getFullYear();
  const chartSize = relatedChart ? projectionData[0].marketSize : data?.CurrentMarketSize;
  const chartCagr = relatedChart ? chartEvidence.cagr_percent : data?.CAGR;
  const chartLabel = relatedChart ? 'Related segment estimate' : (chartEvidence ? 'Market size estimate' : 'Current Market Size');
  const sourceNote = chartEvidence?.source_url
    ? `${chartEvidence.market_definition || ''} ${chartEvidence.cagr_period ? `Forecast period: ${chartEvidence.cagr_period}.` : ''}`.trim()
    : '';
  const sourceLink = chartEvidence?.source_url && /^https?:\/\//i.test(chartEvidence.source_url)
    ? <a href={chartEvidence.source_url} target="_blank" rel="noopener noreferrer">{chartEvidence.source_title || chartEvidence.source_url}</a>
    : null;
  const marketSizeFormatted = formatMarketSize(data?.RegionalDistributionYear ? data?.RegionalDistributionGlobalSize : data?.CurrentMarketSize);
  const headlineYear = data?.RegionalDistributionYear || data?.MarketSizeEvidence?.base_year || new Date().getFullYear();
  const historicalPeriod = data?.RegionalPeriods?.historical || '2019-2024';
  const forecastPeriod = data?.RegionalPeriods?.forecast || data?.MarketSizeEvidence?.cagr_period || '2024-2030';
  const marketName = data?.Market || dataMock?.Market || 'Global market';
  const leftTitleLine1 = `${marketName} `;
  const leftTitleLine2 = `market size in ${headlineYear}`;
  const regionNames = (() => {
    try {
      const names = (distribution?.all || []).map((i) => i.region);
      return Array.from(new Set(names)).join(', ');
    } catch (_) {
      return '';
    }
  })();

  const mockMarketProjection = [
    { year: headlineYear, marketSize: 1000000 },
    { year: headlineYear + 1, marketSize: 1100000 },
    { year: headlineYear + 2, marketSize: 1210000 },
    { year: headlineYear + 3, marketSize: 1331000 },
    { year: headlineYear + 4, marketSize: 1464100 },
    { year: headlineYear + 5, marketSize: 1610510 },
    { year: headlineYear + 6, marketSize: 1771561 }
  ];

  const MockMarketProjection = () => (
    <Box
      sx={{
        p: 3,
        borderRadius: 2,
        bgcolor: 'background.paper',
        position: 'relative',
        isolation: 'isolate',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          padding: '1px',
          borderRadius: 'inherit',
          background: 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
          WebkitMaskComposite: 'xor',
          maskComposite: 'exclude',
          zIndex: -1
        },
        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
        Market Size Projection
      </Typography>
      <Box sx={{ position: 'relative', width: '100%', height: 360 }}>
        <Box sx={{ position: 'absolute', inset: 0, filter: 'blur(6px)', opacity: 0.7 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ReBarChart data={mockMarketProjection} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis tickFormatter={formatMarketSize} />
              <ReTooltip formatter={(v) => formatMarketSize(v)} />
              <ReLegend />
              <ReBar dataKey="marketSize" fill="#8884d8" name="Market Size" />
            </ReBarChart>
          </ResponsiveContainer>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'transparent',
            borderRadius: 2
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              borderRadius: 2,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              maxWidth: 520
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Unlock Market Size Projection
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Get precise market size forecasts and CAGR trends for all regions.
            </Typography>
            <Grid container spacing={1} sx={{ mb: 1 }}>
              <Grid item xs={6}>
                <Typography variant="caption">• Market size projection</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption">• Regional distribution</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption">• Challenges & Obstacles</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption">• Drivers behind growth</Typography>
              </Grid>
            </Grid>
            {regionNames && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Regions: {regionNames}
              </Typography>
            )}
            <Button variant="gradient" color="primary" onClick={handleTrialClick} fullWidth>
              {trialAllowed ? 'Start Free Trial' : 'Upgrade Now'}
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" align="center" sx={{ mt: 0.5 }}>
              Cancel anytime
            </Typography>
          </Box>
        </Box>
      </Box>
      <Chip label="MOCK example, get PRO to get real data" size="small" color="warning" variant="outlined" sx={{ mt: 1 }} />
    </Box>
  );

  const MockGeoDistribution = () => (
    <Box sx={{ display: 'flex', gap: 6, mt: 4, position: 'relative' }}>
      <Box sx={{ width: '25%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {leftTitleLine1}
          </Typography>
          <Typography variant="subtitle1" fontWeight={700}>
            {leftTitleLine2}
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {marketSizeFormatted || '$5.0B'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 6, alignItems: 'flex-start', filter: 'blur(6px)', opacity: 0.7 }}>
          <Legend items={distribution.all} />
          <VerticalBar items={distribution.all} />
        </Box>
        <Footnotes items={[]} />
      </Box>
      <Box sx={{ width: '75%', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '118px 64px 1fr',
              gap: 2,
              alignItems: 'end',
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" fontWeight={700}>
                CAGR
              </Typography>
            </Box>
            <Typography variant="caption" fontWeight={700} align="center">
              {historicalPeriod}
            </Typography>
            <Typography variant="caption" fontWeight={700} align="center" sx={{ borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}>
              {forecastPeriod}
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '118px 64px 1fr', gap: 2, alignItems: 'center', pb: 1 }}>
            <Box />
            <Box />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}>
              <Typography variant="caption" color="text.secondary" align="center">
                Base case¹
              </Typography>
              <Typography variant="caption" color="text.secondary" align="center">
                Downside case²
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ filter: 'blur(6px)', opacity: 0.7 }}>
          {cagrRows.map((row) => (
            <Box key={row.region} sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '102px 80px 1fr', gap: 2, alignItems: 'start' }}>
                <Box sx={{ py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                    {row.region}
                  </Typography>
                  {Number.isFinite(row?.marketSize) && row.marketSize > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {formatMarketSize(row.marketSize)}
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{
                    bgcolor: '#2563eb',
                    color: '#fff',
                    textAlign: 'center',
                    py: 1,
                    px: 1,
                    borderRadius: 1,
                    fontWeight: 700,
                    fontSize: 12,
                    minWidth: 72
                  }}
                >
                  {row.cagr2019}
                </Box>
                <Box
                  sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Typography variant="caption" sx={{ lineHeight: 1.4, flex: 1 }}>
                      {row.baseCaseDesc}
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: '#dbeafe',
                        color: 'text.primary',
                        textAlign: 'center',
                        py: 1,
                        px: 1,
                        borderRadius: 1,
                        fontWeight: 700,
                        fontSize: 12,
                        minWidth: 72
                      }}
                    >
                      {row.baseCase}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <Typography variant="caption" sx={{ lineHeight: 1.4, flex: 1 }}>
                      {row.downsideDesc}
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: '#dbeafe',
                        color: 'text.primary',
                        textAlign: 'center',
                        py: 1,
                        px: 1,
                        borderRadius: 1,
                        fontWeight: 700,
                        fontSize: 12,
                        minWidth: 72
                      }}
                    >
                      {row.downsideCase}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'transparent',
          borderRadius: 2
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            maxWidth: 560
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Unlock Regional Growth Table & Distribution
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            View full CAGR table, base/downside scenarios, and region shares.
          </Typography>
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={6}>
              <Typography variant="caption">• Full regional distribution</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption">• CAGR base/downside cases</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption">• Challenges & Obstacles</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption">• Drivers behind growth</Typography>
            </Grid>
          </Grid>
          {regionNames && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Regions: {regionNames}
            </Typography>
          )}
          <Button variant="gradient" color="primary" onClick={handleTrialClick} fullWidth>
            {trialAllowed ? 'Start Free Trial' : 'Upgrade Now'}
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" align="center" sx={{ mt: 0.5 }}>
            Cancel anytime
          </Typography>
        </Box>
      </Box>
      <Chip
        label="MOCK example, get PRO to get real data"
        size="small"
        color="warning"
        variant="outlined"
        sx={{ position: 'absolute', bottom: -24 }}
      />
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 5, mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 1,
            background: 'linear-gradient(135deg, #0070f3 0%, #6743b5 60%, rgba(103,58,183,0.7) 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'primary.main'
          }}
        >
          Market Overview
        </Typography>
        <Chip label="Powered by Askpot AI" size="small" color="primary" variant="outlined" />
      </Box>

      {(data?.Introduction || data?.['Market Size and Growth']) && (
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: 'background.paper',
              position: 'relative',
              isolation: 'isolate',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                padding: '1px',
                borderRadius: 'inherit',
                background: 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
                zIndex: -1
              },
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mt: 0.5 }} />
              <Box>
                {data?.Introduction && (
                  <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                    {data.Introduction}
                  </Typography>
                )}
                {data?.['Market Size and Growth'] && (
                  <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                    {data['Market Size and Growth']}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {projectionData.length > 0 && (
        <Box sx={{ mt: 3 }}>
          {isProUser ? (
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: 'background.paper',
                position: 'relative',
                isolation: 'isolate',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  padding: '1px',
                  borderRadius: 'inherit',
                  background: 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  zIndex: -1
                },
                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                Market Size Projection
              </Typography>
              <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 1 }}>
                {chartLabel}{chartEvidence ? ` (${chartBaseYear})` : ''}: {formatMarketSize(chartSize)} | CAGR:{' '}
                {typeof chartCagr === 'number' ? `${Number(chartCagr).toFixed(2)}%` : 'N/A'}
              </Typography>
              <Box sx={{ position: 'relative', width: '100%', height: 360 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ReBarChart data={projectionData} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis tickFormatter={formatMarketSize} />
                    <ReTooltip formatter={(v) => formatMarketSize(v)} />
                    <ReLegend />
                    <ReBar dataKey="marketSize" fill="#8884d8" name="Market Size" />
                  </ReBarChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 24,
                    display: 'flex',
                    alignItems: 'center',
                    pointerEvents: 'none',
                    px: 1,
                    pb: 5.5,
                    borderRadius: 1,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
                  }}
                >
                  <Typography variant="caption" color="textSecondary" mr={1}>
                    Powered by
                  </Typography>
                  <img src="/favicon.svg" alt="Askpot Logo" style={{ height: 20 }} />
                  <Typography variant="h5" ml={1}>
                    Askpot
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <MockMarketProjection />
          )}
        </Box>
      )}

      {(data?.Headline || dataMock?.Headline) && (
        <Box sx={{ mt: 4, mb: 2 }}>
          <Typography variant="h3" fontWeight={800} lineHeight={1.2} maxWidth={1000}>
            {data?.Headline || dataMock.Headline}
          </Typography>
        </Box>
      )}

      {isDesktop &&
        (isProUser ? (
          <Box sx={{ display: 'flex', gap: 6, mt: 4 }}>
            {/* Left Section - Bar Chart */}
            <Box sx={{ width: '25%', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {leftTitleLine1}
                </Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                  {leftTitleLine2}
                </Typography>
                <Typography variant="h5" fontWeight={800}>
                  {marketSizeFormatted}
                </Typography>
              </Box>

              {isProUser ? (
                <Box sx={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  <Legend items={distribution.all} />
                  <VerticalBar items={distribution.all} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 6, alignItems: 'flex-start', filter: 'blur(6px)', opacity: 0.7, position: 'relative' }}>
                  <Legend items={distribution.all} />
                  <VerticalBar items={distribution.all} />
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: 'rgba(255,255,255,0.6)',
                      borderRadius: 2,
                      cursor: 'pointer',
                      opacity: 0,
                      transition: 'opacity 0.3s',
                      '&:hover': { opacity: 1 }
                    }}
                    onClick={handleTrialClick}
                  >
                    <LockIcon />
                    <Typography variant="caption" color="primary" ml={1}>
                      Unlock
                    </Typography>
                  </Box>
                </Box>
              )}

              <Footnotes items={distribution.footnotes} />
            </Box>

            {/* Right Section - CAGR Table */}
            <Box sx={{ width: '75%', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '118px 64px 1fr',
                    gap: 2,
                    alignItems: 'end',
                    pb: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* <Box sx={{ width: 16, height: 16, bgcolor: '#2563eb' }} /> */}
                    <Typography variant="caption" fontWeight={700}>
                      CAGR
                    </Typography>
                  </Box>
                  <Typography variant="caption" fontWeight={700} align="center">
                    {historicalPeriod}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    align="center"
                    sx={{ borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}
                  >
                    {forecastPeriod}
                  </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '118px 64px 1fr', gap: 2, alignItems: 'center', pb: 1 }}>
                  <Box />
                  <Box />
                  <Box
                    sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, borderLeft: '1px solid', borderColor: 'divider', pl: 2 }}
                  >
                    <Typography variant="caption" color="text.secondary" align="center">
                      Base case¹
                    </Typography>
                    <Typography variant="caption" color="text.secondary" align="center">
                      Downside case²
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box>
                {cagrRows.map((row) => (
                  <Box key={row.region} sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '102px 80px 1fr', gap: 2, alignItems: 'start' }}>
                      <Box sx={{ py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                          {row.region}
                        </Typography>
                        {Number.isFinite(row?.marketSize) && row.marketSize > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {formatMarketSize(row.marketSize)}
                          </Typography>
                        )}
                      </Box>
                      <Box
                        sx={{
                          bgcolor: '#2563eb',
                          color: '#fff',
                          textAlign: 'center',
                          py: 1,
                          px: 1,
                          borderRadius: 1,
                          fontWeight: 700,
                          fontSize: 12,
                          minWidth: 72
                        }}
                      >
                        {row.cagr2019}
                      </Box>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: 2,
                          borderLeft: '1px solid',
                          borderColor: 'divider',
                          pl: 2
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <Typography variant="caption" sx={{ lineHeight: 1.4, flex: 1 }}>
                            {row.baseCaseDesc}
                          </Typography>
                          <Box
                            sx={{
                              bgcolor: '#dbeafe',
                              color: 'text.primary',
                              textAlign: 'center',
                              py: 1,
                              px: 1,
                              borderRadius: 1,
                              fontWeight: 700,
                              fontSize: 12,
                              minWidth: 72
                            }}
                          >
                            {row.baseCase}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <Typography variant="caption" sx={{ lineHeight: 1.4, flex: 1 }}>
                            {row.downsideDesc}
                          </Typography>
                          <Box
                            sx={{
                              bgcolor: '#dbeafe',
                              color: 'text.primary',
                              textAlign: 'center',
                              py: 1,
                              px: 1,
                              borderRadius: 1,
                              fontWeight: 700,
                              fontSize: 12,
                              minWidth: 72
                            }}
                          >
                            {row.downsideCase}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ) : (
          <MockGeoDistribution />
        ))}

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ mt: 3, mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Challenges & Obstacles
            </Typography>
          </Box>
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: 'background.paper',
              position: 'relative',
              isolation: 'isolate',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                padding: '1px',
                borderRadius: 'inherit',
                background: 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
                zIndex: -1
              },
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.25 }}>
              {isProUser
                ? (data?.ChallengesList || dataMock?.ChallengesList || []).map((c, idx) => (
                    <Box key={idx} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.75 }}>
                        {c.title}
                      </Typography>
                      <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                        <ChevronRight sx={{ color: 'primary.main', fontSize: 20, verticalAlign: 'middle' }} /> {c.text}
                      </Typography>
                    </Box>
                  ))
                : (() => {
                    const items = data?.ChallengesList || dataMock?.ChallengesList || [];
                    const visible = items.slice(0, 1);
                    const gated = items.slice(1);
                    return (
                      <>
                        {visible.map((c, idx) => (
                          <Box key={`vis-${idx}`} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.75 }}>
                              {c.title}
                            </Typography>
                            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                              <ChevronRight sx={{ color: 'primary.main', fontSize: 20, verticalAlign: 'middle' }} /> {c.text}
                            </Typography>
                          </Box>
                        ))}
                        {gated.map((c, idx) => (
                          <Box key={`gate-${idx}`} sx={{ position: 'relative' }}>
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                filter: 'blur(5px)',
                                opacity: 0.75
                              }}
                            >
                              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.75 }}>
                                {c.title}
                              </Typography>
                              <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                <ChevronRight sx={{ color: 'primary.main', fontSize: 20, verticalAlign: 'middle' }} /> {c.text}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: 'rgba(255,255,255,0.6)',
                                borderRadius: 2,
                                cursor: 'pointer',
                                opacity: 0,
                                transition: 'opacity 0.3s',
                                '&:hover': { opacity: 1 }
                              }}
                              onClick={handleTrialClick}
                            >
                              <LockIcon />
                              <Typography variant="caption" color="primary" ml={1}>
                                Unlock
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </>
                    );
                  })()}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ mt: 3, mb: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Drivers behind the market growth
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: 'background.paper',
                position: 'relative',
                isolation: 'isolate',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  padding: '1px',
                  borderRadius: 'inherit',
                  background: 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  zIndex: -1
                },
                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
              }}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.25 }}>
                {isProUser
                  ? (data?.DriversList || dataMock?.DriversList || []).map((d, idx) => (
                      <Box key={idx} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.75 }}>
                          {d.title}
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                          <ChevronRight sx={{ color: 'primary.main', fontSize: 20, verticalAlign: 'middle' }} /> {d.text}
                        </Typography>
                      </Box>
                    ))
                  : (() => {
                      const items = data?.DriversList || dataMock?.DriversList || [];
                      const visible = items.slice(0, 1);
                      const gated = items.slice(1);
                      return (
                        <>
                          {visible.map((d, idx) => (
                            <Box key={`vis-${idx}`} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.75 }}>
                                {d.title}
                              </Typography>
                              <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                <ChevronRight sx={{ color: 'primary.main', fontSize: 20, verticalAlign: 'middle' }} /> {d.text}
                              </Typography>
                            </Box>
                          ))}
                          {gated.map((d, idx) => (
                            <Box key={`gate-${idx}`} sx={{ position: 'relative' }}>
                              <Box
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  filter: 'blur(5px)',
                                  opacity: 0.75
                                }}
                              >
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.75 }}>
                                  {d.title}
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                                  <ChevronRight sx={{ color: 'primary.main', fontSize: 20, verticalAlign: 'middle' }} /> {d.text}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  backgroundColor: 'rgba(255,255,255,0.6)',
                                  borderRadius: 2,
                                  cursor: 'pointer',
                                  opacity: 0,
                                  transition: 'opacity 0.3s',
                                  '&:hover': { opacity: 1 }
                                }}
                                onClick={handleTrialClick}
                              >
                                <LockIcon />
                                <Typography variant="caption" color="primary" ml={1}>
                                  Unlock
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </>
                      );
                    })()}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {data?.Footnotes?.line1 || dataMock?.Footnotes?.line1}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {data?.Footnotes?.line2 || dataMock?.Footnotes?.line2} {sourceNote}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption">{sourceLink || data?.Footnotes?.source || dataMock?.Footnotes?.source}</Typography>
          <Typography variant="caption" fontWeight={700}>
            Askpot AI
          </Typography>
        </Box>
      </Box>

      {!isProUser && (
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: 3,
            backdropFilter: 'blur(5px)',
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            textAlign: 'center',
            borderRadius: 2,
            mt: 2
          }}
        >
          <Typography variant="h6" color="primary" align="center" mb={1}>
            Start your FREE 7 days trial to access the full Market Overview
          </Typography>
          <Button variant="contained" color="primary" sx={{ mt: 2 }} onClick={handleTrialClick}>
            Unlock Market Overview
          </Button>
        </Paper>
      )}
    </Box>
  );
}
